import React, { useState } from 'react';
import {
  FiCpu, FiRefreshCw, FiZap, FiAlertCircle, FiCheckCircle,
  FiAlertTriangle, FiClipboard, FiX, FiMail, FiEdit3,
} from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';

// ─── Spam Bar ─────────────────────────────────────────────────────────────────
const SpamBar = ({ score }) => {
  const pct = `${score * 10}%`;
  const color = score <= 2 ? 'bg-green-500' : score <= 4 ? 'bg-blue-500' : score <= 6 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = score <= 2 ? 'text-green-700' : score <= 4 ? 'text-blue-700' : score <= 6 ? 'text-amber-700' : 'text-red-700';
  const label = score <= 2 ? 'Safe' : score <= 4 ? 'Low Risk' : score <= 6 ? 'Medium Risk' : score <= 8 ? 'High Risk' : 'Very High Risk';
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-neutral-500">Spam Risk</span>
        <span className={`font-bold ${textColor}`}>{label} ({score}/10)</span>
      </div>
      <div className="w-full h-2.5 bg-neutral-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: pct }} />
      </div>
    </div>
  );
};

// ─── Score Tile ───────────────────────────────────────────────────────────────
const MiniScore = ({ label, value }) => {
  const color = value >= 8 ? 'text-green-600 bg-green-50 border-green-200' : value >= 6 ? 'text-blue-600 bg-blue-50 border-blue-200' : value >= 4 ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-red-600 bg-red-50 border-red-200';
  return (
    <div className={`flex flex-col items-center justify-center rounded-xl border p-3 ${color}`}>
      <span className="text-2xl font-bold">{value}</span>
      <span className="text-xs font-medium mt-0.5">{label}</span>
    </div>
  );
};

// ─── Tone pills ───────────────────────────────────────────────────────────────
const TONES = [
  { id: 'Professional', emoji: '💼', desc: 'Polished & business-ready' },
  { id: 'Friendly',     emoji: '😊', desc: 'Warm & conversational' },
  { id: 'Persuasive',   emoji: '🎯', desc: 'Benefit-driven & action-focused' },
  { id: 'Concise',      emoji: '✂️', desc: 'Short & to the point' },
  { id: 'Formal',       emoji: '🎩', desc: 'Strict formal register' },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const AIEmailHelper = ({ subject, body, onClose, onSelectSubject, onApplyBody }) => {
  const [activeTab, setActiveTab] = useState('check');

  // Email checker state
  const [checkResult, setCheckResult]   = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkError, setCheckError]     = useState('');

  // Subject generator state
  const [subjectTopic,    setSubjectTopic]    = useState('');
  const [subjectAudience, setSubjectAudience] = useState('');
  const [suggestions,     setSuggestions]     = useState([]);
  const [suggestLoading,  setSuggestLoading]  = useState(false);
  const [suggestError,    setSuggestError]    = useState('');

  // Rephrase body state
  const [selectedTone,     setSelectedTone]     = useState('Professional');
  const [rephraseResult,   setRephraseResult]   = useState(null);
  const [rephraseLoading,  setRephraseLoading]  = useState(false);
  const [rephraseError,    setRephraseError]    = useState('');
  const [showPreviewHtml,  setShowPreviewHtml]  = useState(false);

  // ── Email checker ──────────────────────────────────────────────────────────
  const handleCheck = async () => {
    if (!subject || !body) {
      setCheckError('Please enter a subject and body in the email form first.');
      return;
    }
    setCheckLoading(true); setCheckError(''); setCheckResult(null);
    try {
      const res = await axios.post('/api/ai/check-email', { subject, body });
      setCheckResult(res.data.result);
    } catch (err) {
      setCheckError(err.response?.data?.error || 'Check failed. Please try again.');
    } finally {
      setCheckLoading(false);
    }
  };

  // ── Subject suggester ──────────────────────────────────────────────────────
  const handleSuggest = async () => {
    if (!subjectTopic.trim()) { setSuggestError('Please enter a topic.'); return; }
    setSuggestLoading(true); setSuggestError(''); setSuggestions([]);
    try {
      const res = await axios.post('/api/ai/suggest-subjects', {
        topic: subjectTopic, audienceHint: subjectAudience, count: 6,
      });
      setSuggestions(Array.isArray(res.data.suggestions) ? res.data.suggestions : []);
    } catch (err) {
      setSuggestError(err.response?.data?.error || 'Generation failed. Please try again.');
    } finally {
      setSuggestLoading(false);
    }
  };

  // ── Rephrase body ──────────────────────────────────────────────────────────
  const handleRephrase = async () => {
    if (!body) { setRephraseError('Please write your email body first.'); return; }
    setRephraseLoading(true); setRephraseError(''); setRephraseResult(null); setShowPreviewHtml(false);
    try {
      const res = await axios.post('/api/ai/rephrase-email', {
        subject: subject || '',
        body,
        tone: selectedTone,
      });
      setRephraseResult(res.data.result);
    } catch (err) {
      setRephraseError(err.response?.data?.error || 'Rephrase failed. Please try again.');
    } finally {
      setRephraseLoading(false);
    }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

  const handleApplyBody = () => {
    if (onApplyBody && rephraseResult?.rephrasedHtml) {
      onApplyBody(rephraseResult.rephrasedHtml);
      toast.success('Body applied to email!');
      onClose();
    }
  };

  const tabs = [
    { id: 'check',   label: '🔍 Email Checker' },
    { id: 'suggest', label: '✨ Subject Generator' },
    { id: 'rephrase',label: '✍️ Rephrase Body' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-primary-600 to-accent-600 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <FiCpu className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-white font-bold">AI Email Assistant</h2>
          </div>
          <button onClick={onClose} className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-all">
            <FiX className="w-4 h-4" />
          </button>
        </div>

        {/* Tab nav */}
        <div className="flex border-b border-neutral-200 px-6 pt-2 flex-shrink-0 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-all duration-200 mr-1 ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 p-6 space-y-4">

          {/* ── Email Checker Tab ── */}
          {activeTab === 'check' && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-500">
                Analyzes your current email for spam risks, content quality, and actionable improvements before you send.
              </p>
              <div className="flex gap-2 items-center">
                <div className="flex-1 px-3 py-2 bg-neutral-100 border border-neutral-200 rounded-lg text-sm text-neutral-600 truncate">
                  <span className="text-neutral-400 mr-1">Subject:</span>
                  {subject || <span className="italic text-neutral-400">No subject yet</span>}
                </div>
                <button
                  onClick={handleCheck}
                  disabled={checkLoading || !subject || !body}
                  className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  {checkLoading ? <><FiRefreshCw className="w-4 h-4 animate-spin" />Checking…</> : <><FiZap className="w-4 h-4" />Check Email</>}
                </button>
              </div>

              {checkError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  <FiAlertCircle className="w-4 h-4 flex-shrink-0" /> {checkError}
                </div>
              )}
              {checkLoading && (
                <div className="flex flex-col items-center py-10 gap-3">
                  <div className="w-12 h-12 rounded-full border-4 border-primary-100 border-t-primary-600 animate-spin" />
                  <p className="text-neutral-500 text-sm">AI is reviewing your email…</p>
                </div>
              )}
              {!checkLoading && checkResult && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <MiniScore label="Overall"  value={checkResult.overallScore || 0} />
                    <MiniScore label="Subject"  value={checkResult.subjectAnalysis?.score || 0} />
                    <MiniScore label="Content"  value={checkResult.contentAnalysis?.clarity || 0} />
                  </div>
                  <SpamBar score={checkResult.spamScore || 0} />
                  {checkResult.subjectAnalysis && (
                    <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-100">
                      <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Subject Line</p>
                      <p className="text-sm text-neutral-700">{checkResult.subjectAnalysis.feedback}</p>
                      {checkResult.subjectAnalysis.improvements?.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {checkResult.subjectAnalysis.improvements.map((imp, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-amber-700">
                              <FiAlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{imp}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  {checkResult.contentAnalysis && (
                    <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-100">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Content</p>
                        <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md font-medium">
                          Tone: {checkResult.contentAnalysis.tone}
                        </span>
                      </div>
                      <p className="text-sm text-neutral-700">{checkResult.contentAnalysis.feedback}</p>
                    </div>
                  )}
                  {checkResult.spamTriggers?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">⚠️ Spam Triggers Found</p>
                      <div className="flex flex-wrap gap-2">
                        {checkResult.spamTriggers.map((t, i) => (
                          <span key={i} className="px-2 py-0.5 bg-red-100 text-red-700 rounded-md text-xs font-medium">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {checkResult.improvements?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Suggested Improvements</p>
                      {checkResult.improvements.map((imp, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-100 rounded-xl">
                          <span className="px-2 py-0.5 bg-blue-200 text-blue-800 rounded text-xs font-bold flex-shrink-0">{imp.area}</span>
                          <p className="text-sm text-blue-900">{imp.suggestion}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Subject Generator Tab ── */}
          {activeTab === 'suggest' && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-500">
                Describe your campaign and get AI-generated subject line ideas optimised for open rates.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Campaign Topic / Goal <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={subjectTopic}
                    onChange={(e) => setSubjectTopic(e.target.value)}
                    placeholder="e.g. Exclusive 30% discount for returning customers"
                    className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none"
                    onKeyDown={(e) => e.key === 'Enter' && handleSuggest()}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Target Audience <span className="text-neutral-400">(optional)</span></label>
                  <input
                    type="text"
                    value={subjectAudience}
                    onChange={(e) => setSubjectAudience(e.target.value)}
                    placeholder="e.g. SaaS startup founders, e-commerce shoppers"
                    className="w-full px-3 py-2.5 border border-neutral-300 rounded-lg text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none"
                  />
                </div>
                <button
                  onClick={handleSuggest}
                  disabled={suggestLoading || !subjectTopic.trim()}
                  className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  {suggestLoading ? <><FiRefreshCw className="w-4 h-4 animate-spin" />Generating…</> : <><FiZap className="w-4 h-4" />Generate Subject Lines</>}
                </button>
              </div>
              {suggestError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  <FiAlertCircle className="w-4 h-4 flex-shrink-0" />{suggestError}
                </div>
              )}
              {suggestLoading && (
                <div className="flex flex-col items-center py-10 gap-3">
                  <div className="w-12 h-12 rounded-full border-4 border-primary-100 border-t-primary-600 animate-spin" />
                  <p className="text-neutral-500 text-sm">AI is crafting subject lines…</p>
                </div>
              )}
              {!suggestLoading && suggestions.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Generated Subject Lines</p>
                  {suggestions.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 bg-neutral-50 border border-neutral-200 rounded-xl group hover:border-primary-300 hover:bg-primary-50/30 transition-all">
                      <FiMail className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                      <span className="flex-1 text-sm text-neutral-800">{s}</span>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => copyToClipboard(s)} className="p-1.5 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-200 rounded-lg transition-all" title="Copy">
                          <FiClipboard className="w-3.5 h-3.5" />
                        </button>
                        {onSelectSubject && (
                          <button onClick={() => { onSelectSubject(s); toast.success('Subject applied!'); }} className="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-100 rounded-lg transition-all" title="Use this subject">
                            <FiCheckCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Rephrase Body Tab ── */}
          {activeTab === 'rephrase' && (
            <div className="space-y-5">
              <p className="text-sm text-neutral-500">
                AI rewrites your email body in the tone you choose — keeping the same message while improving clarity, engagement, and deliverability.
              </p>

              {/* Tone selector */}
              <div>
                <p className="text-xs font-semibold text-neutral-600 uppercase tracking-wide mb-3">Choose a Tone</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {TONES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setSelectedTone(t.id)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all duration-150 ${
                        selectedTone === t.id
                          ? 'border-primary-500 bg-primary-50 shadow-sm'
                          : 'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50'
                      }`}
                    >
                      <span className="text-xl flex-shrink-0">{t.emoji}</span>
                      <div>
                        <p className={`text-sm font-semibold ${selectedTone === t.id ? 'text-primary-700' : 'text-neutral-800'}`}>{t.id}</p>
                        <p className="text-xs text-neutral-500">{t.desc}</p>
                      </div>
                      {selectedTone === t.id && (
                        <FiCheckCircle className="w-4 h-4 text-primary-500 ml-auto flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Current body preview (collapsed) */}
              {body && (
                <div className="bg-neutral-50 rounded-xl border border-neutral-200 p-4">
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Current Body (plain text preview)</p>
                  <p className="text-sm text-neutral-600 line-clamp-3">
                    {body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 250)}…
                  </p>
                </div>
              )}

              <button
                onClick={handleRephrase}
                disabled={rephraseLoading || !body}
                className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg text-sm font-semibold hover:from-violet-600 hover:to-purple-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-medium"
              >
                {rephraseLoading
                  ? <><FiRefreshCw className="w-4 h-4 animate-spin" />Rephrasing…</>
                  : <><FiEdit3 className="w-4 h-4" />Rephrase in {selectedTone} Tone</>}
              </button>

              {rephraseError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  <FiAlertCircle className="w-4 h-4 flex-shrink-0" />{rephraseError}
                </div>
              )}

              {rephraseLoading && (
                <div className="flex flex-col items-center py-10 gap-3">
                  <div className="w-12 h-12 rounded-full border-4 border-violet-100 border-t-violet-600 animate-spin" />
                  <p className="text-neutral-500 text-sm">AI is rewriting your email…</p>
                  <p className="text-neutral-400 text-xs">This may take 20–40 seconds</p>
                </div>
              )}

              {!rephraseLoading && rephraseResult && (
                <div className="space-y-4">
                  {/* Tone badge */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Rephrased in</span>
                    <span className="px-2.5 py-1 bg-violet-50 border border-violet-200 text-violet-700 rounded-lg text-xs font-bold">
                      {TONES.find(t => t.id === rephraseResult.toneApplied)?.emoji} {rephraseResult.toneApplied}
                    </span>
                  </div>

                  {/* What changed */}
                  {rephraseResult.changes?.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">✅ What was improved</p>
                      <ul className="space-y-1">
                        {rephraseResult.changes.map((c, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-green-800">
                            <FiCheckCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-green-500" />{c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Rephrased text preview */}
                  <div className="bg-neutral-50 rounded-xl border border-neutral-200 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-neutral-200 bg-white">
                      <p className="text-xs font-semibold text-neutral-600">Rephrased Body Preview</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowPreviewHtml(!showPreviewHtml)}
                          className="text-xs text-neutral-500 hover:text-neutral-700 px-2 py-1 rounded hover:bg-neutral-100 transition-all"
                        >
                          {showPreviewHtml ? 'Plain text' : 'HTML preview'}
                        </button>
                        <button
                          onClick={() => copyToClipboard(rephraseResult.rephrasedText)}
                          className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-700 px-2 py-1 rounded hover:bg-neutral-100 transition-all"
                          title="Copy plain text"
                        >
                          <FiClipboard className="w-3 h-3" /> Copy
                        </button>
                      </div>
                    </div>
                    <div className="p-4 max-h-52 overflow-y-auto">
                      {showPreviewHtml ? (
                        <div
                          className="text-sm"
                          dangerouslySetInnerHTML={{ __html: rephraseResult.rephrasedHtml }}
                        />
                      ) : (
                        <p className="text-sm text-neutral-700 whitespace-pre-wrap leading-relaxed">
                          {rephraseResult.rephrasedText}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => copyToClipboard(rephraseResult.rephrasedText)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-neutral-100 text-neutral-700 rounded-lg text-sm font-medium hover:bg-neutral-200 transition-all"
                    >
                      <FiClipboard className="w-4 h-4" /> Copy to Clipboard
                    </button>
                    {onApplyBody && (
                      <button
                        onClick={handleApplyBody}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg text-sm font-semibold hover:from-violet-600 hover:to-purple-700 transition-all shadow-medium"
                      >
                        <FiCheckCircle className="w-4 h-4" /> Apply to Email
                      </button>
                    )}
                  </div>

                  {onApplyBody && (
                    <p className="text-xs text-neutral-400 text-center">
                      "Apply to Email" replaces your current body. You can undo by re-typing or running Rephrase again.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AIEmailHelper;
